import { http } from './http';
import { clearAuth, getItem, getOrCreateDeviceUuid, setItem } from '../lib/storage';

type RegisterResponse = {
  success: boolean;
  user_id: string;
  access_token: string;
  refresh_token: string;
};

type RefreshResponse = {
  access_token: string;
  refresh_token: string;
};

function parseJwtExpiry(token: string): number | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(atob(parts[1] ?? '')) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function isAccessTokenValid(): boolean {
  const token = getItem('accessToken');
  if (!token || token.length < 10) return false;
  const expiry = parseJwtExpiry(token);
  if (expiry === null) return false;
  return expiry > Date.now() + 30_000;
}

async function register(): Promise<void> {
  const language = navigator.language.slice(0, 2);
  const browserLabel = `Web (${navigator.userAgent.split(' ')[0] ?? 'browser'})`;
  const response = await http<RegisterResponse>('/user/register', {
    method: 'POST',
    body: {
      device_uuid: getOrCreateDeviceUuid(),
      language,
      name: getItem('watcherName') ?? browserLabel,
    },
  });
  if (!response.success) throw new Error('register failed');
  setItem('userId', response.user_id);
  setItem('accessToken', response.access_token);
  setItem('refreshToken', response.refresh_token);
  setItem('language', language);
}

async function refresh(): Promise<void> {
  const userId = getItem('userId');
  const refreshToken = getItem('refreshToken');
  if (!userId || !refreshToken) throw new Error('no refresh token');
  const response = await http<RefreshResponse>('/user/refresh-token', {
    method: 'POST',
    body: { user_id: userId, refresh_token: refreshToken },
  });
  if (!response.refresh_token) throw new Error('refresh failed');
  setItem('accessToken', response.access_token);
  setItem('refreshToken', response.refresh_token);
}

export async function ensureSession(): Promise<void> {
  if (isAccessTokenValid()) return;
  try {
    await refresh();
    return;
  } catch {
    clearAuth();
  }
  await register();
}

export async function updateName(name: string): Promise<void> {
  await http('/user', {
    method: 'PUT',
    body: { name },
    auth: true,
  });
  setItem('watcherName', name);
}
